import { useEffect, useRef, useState } from 'react';
import type { Account, AccountStatus, CalendarInfo, Me, ProviderPreset, PushPreview, Settings as SettingsT } from '@momomail/shared';
import { api, bridge, errorText } from '../lib/bridge';
import { timeAgo } from '../lib/format';
import { locale, LANGUAGES, t, tMessage } from '../lib/i18n';
import { AccountForm, ProviderPicker } from './AccountForm';
import { StorageSettings } from './Storage';
import { useUpdates } from './Updates';
import { AiSettings } from './Ai';
import { CalendarSettings } from './Calendar';
import { ConnectionSettings } from './Connection';
import { Icon, type IconName } from './Icon';
import { confirmDialog, Dialog } from './Overlay';
import { toast } from './Toasts';
import { UserAdmin, useMe } from './UserSettings';
import { UpdateSettings } from './Updates';

export const SETTINGS_TABS = ['accounts', 'ai', 'calendar', 'storage', 'users', 'notifications', 'appearance', 'general', 'connection', 'shortcuts'] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

/** Settings page tabs (read at render time, so they follow the language). */
const TABS = (): { id: SettingsTab; label: string; icon: IconName }[] => [
  { id: 'accounts', label: t('账户'), icon: 'mail' },
  { id: 'ai', label: 'AI', icon: 'zap' },
  { id: 'calendar', label: t('日历'), icon: 'calendar' },
  { id: 'storage', label: t('存储'), icon: 'layers' },
  { id: 'connection', label: t('账号与连接'), icon: 'user' },
  { id: 'users', label: t('用户管理'), icon: 'users' },
  { id: 'notifications', label: t('通知'), icon: 'bell' },
  { id: 'appearance', label: t('外观'), icon: 'palette' },
  { id: 'general', label: t('通用'), icon: 'sliders' },
  { id: 'shortcuts', label: t('快捷键'), icon: 'keyboard' },
];

type TabFlag = { level: 'error' | 'warn'; title: string };

/**
 * Problems to flag on the settings tabs (red = something is broken, yellow = worth a look), so they show without opening
 * each tab: a mailbox whose login expired, a Google Calendar that needs reconnecting, paused notifications, an update.
 */
function useTabFlags(open: boolean, accounts: Account[], statuses: Map<string, AccountStatus>, settings: SettingsT): Partial<Record<SettingsTab, TabFlag>> {
  const [calendar, setCalendar] = useState<CalendarInfo | null>(null);
  const updates = useUpdates();
  useEffect(() => {
    if (!open) return;
    let active = true;
    api()
      .calendar.info()
      .then((c) => active && setCalendar(c))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open]);
  const flags: Partial<Record<SettingsTab, TabFlag>> = {};
  const broken = accounts.filter((a) => statuses.get(a.id)?.authError);
  if (broken.length) flags.accounts = { level: 'error', title: t('{n} 个邮箱登录失效', { n: broken.length }) };
  // Calendar accounts: red when one needs signing in again, yellow for other sync errors
  const calAccounts = calendar?.accounts ?? [];
  const relogin = calAccounts.filter((a) => a.needsAuth).length;
  if (relogin) flags.calendar = { level: 'error', title: t('{n} 个日历账户要重新登录', { n: relogin }) };
  else if (calAccounts.some((a) => a.error)) flags.calendar = { level: 'warn', title: t('日历同步出错') };
  if (settings.notificationsPausedUntil != null && settings.notificationsPausedUntil > Date.now()) flags.notifications = { level: 'warn', title: t('通知已暂停') };
  if (updates?.status === 'ready' || updates?.status === 'downloading') flags.general = { level: 'warn', title: t('有新版本') };
  return flags;
}

/** Keyboard shortcut table (read at render time, so it follows the language). */
export const SHORTCUTS = (): [string[], string][] => [
  [['J', '↓'], t('下一封')],
  [['K', '↑'], t('上一封')],
  [['C'], t('写邮件')],
  [['G', 'M'], t('切换到邮件')],
  [['G', 'C'], t('切换到日历')],
  [['R'], t('回复')],
  [['A'], t('全部回复')],
  [['F'], t('转发')],
  [['E'], t('归档')],
  [['Delete', '#'], t('删除')],
  [['S'], t('加 / 取消星标')],
  [['U'], t('标为已读 / 未读')],
  [['Shift', 'I'], t('当前列表全部标为已读')],
  [['V'], t('移动到…')],
  [['/'], t('搜索')],
  [['Esc'], t('清除搜索 / 关闭对话框')],
  [['F5'], t('立即收信')],
  [['Ctrl', 'Enter'], t('发送（写信时）')],
  [['Ctrl', 'S'], t('存草稿（写信时）')],
  [['Ctrl', ','], t('设置')],
  [['?'], t('显示快捷键')],
];

export function ShortcutTable() {
  return (
    <table className="shortcuts">
      <tbody>
        {SHORTCUTS().map(([keys, label]) => (
          <tr key={label}>
            <td>
              {keys.map((k, i) => (
                <span key={k}>
                  {i > 0 && <span className="shortcuts__or">{keys[0] === 'Shift' || keys[0] === 'Ctrl' ? '+' : keys[0] === 'G' ? '→' : '/'}</span>}
                  <kbd>{k}</kbd>
                </span>
              ))}
            </td>
            <td>{label}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Row({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="setting">
      <div className="setting__text">
        <div className="setting__title">{title}</div>
        {desc && <div className="setting__desc muted">{desc}</div>}
      </div>
      <div className="setting__control">{children}</div>
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button className="switch" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} />;
}

export function SettingsDialog({
  open,
  tab,
  onTab,
  onClose,
  accounts,
  statuses,
  presets,
  settings,
  onSettings,
  editAccount,
  onEditAccount,
}: {
  open: boolean;
  tab: SettingsTab;
  onTab: (t: SettingsTab) => void;
  onClose: () => void;
  accounts: Account[];
  statuses: Map<string, AccountStatus>;
  presets: ProviderPreset[];
  settings: SettingsT;
  onSettings: (s: SettingsT) => void;
  /** Account being edited; 'new' means creating one (pick a provider first). */
  editAccount: Account | 'new' | null;
  onEditAccount: (a: Account | 'new' | null) => void;
}) {
  const [picked, setPicked] = useState<ProviderPreset | null>(null);
  const loadedMe = useMe(open);
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => setMe(loadedMe), [loadedMe]);
  // User management is admin-only (not available in local mode or on older backends)
  const tabs = TABS().filter((x) => (x.id === 'users' ? !!me && !me.local && me.isAdmin : true));
  const flags = useTabFlags(open, accounts, statuses, settings);
  // Opening settings while something is broken: start on that tab (unless the caller already asked for one that's broken)
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current && flags[tab]?.level !== 'error') {
      const broken = tabs.find((x) => flags[x.id]?.level === 'error');
      if (broken && !editAccount) onTab(broken.id);
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (editAccount !== 'new') setPicked(null);
  }, [editAccount]);

  const update = async (patch: Partial<SettingsT>) => {
    try {
      onSettings(await api().settings.update(patch));
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const remove = async (a: Account) => {
    const choice = await confirmDialog({
      title: t('删除账户"{name}"？', { name: a.name }),
      message: (
        <>
          {t('只会从馍馍邮箱里移除这个账户和本机缓存的邮件，')}
          <b>{t('服务器上的邮件不受影响')}</b>
          {t('。以后可以重新添加。')}
        </>
      ),
      confirm: t('删除账户'),
      danger: true,
    });
    if (choice !== 'confirm') return;
    try {
      await api().accounts.remove(a.id);
      toast(t('已移除 {email}', { email: a.email }), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const paused = settings.notificationsPausedUntil != null && settings.notificationsPausedUntil > Date.now();
  const pausedText = !paused
    ? ''
    : settings.notificationsPausedUntil! >= Number.MAX_SAFE_INTEGER - 1
      ? t('已暂停，直到你恢复')
      : t('已暂停到 {time}', { time: new Date(settings.notificationsPausedUntil!).toLocaleString(locale(), { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) });

  let body: React.ReactNode;
  if (tab === 'accounts' && editAccount) {
    const preset = editAccount === 'new' ? picked : (presets.find((p) => p.id === editAccount.provider) ?? presets.at(-1)!);
    body = (
      <div className="settings__section">
        <button className="settings__back" onClick={() => (editAccount === 'new' && picked ? setPicked(null) : onEditAccount(null))}>
          <Icon name="back" size={15} /> {editAccount === 'new' && picked ? t('换个服务商') : t('所有账户')}
        </button>
        <h3 className="settings__h">{editAccount === 'new' ? (picked ? t('添加 {provider} 账户', { provider: tMessage(picked.label) }) : t('添加账户：选一个邮箱服务商')) : t('编辑 {email}', { email: editAccount.email })}</h3>
        {preset ? (
          <AccountForm key={`${editAccount === 'new' ? 'new' : editAccount.id}-${preset.id}`} presets={presets} preset={preset} account={editAccount === 'new' ? null : editAccount} onCancel={() => (editAccount === 'new' ? setPicked(null) : onEditAccount(null))} onSaved={() => onEditAccount(null)} />
        ) : (
          <ProviderPicker presets={presets} onPick={setPicked} />
        )}
      </div>
    );
  } else if (tab === 'accounts') {
    body = (
      <div className="settings__section">
        <h3 className="settings__h">{t('邮箱账户')}</h3>
        {accounts.length === 0 && <p className="muted">{t('还没有账户。')}</p>}
        <ul className="account-cards">
          {accounts.map((a) => {
            const s = statuses.get(a.id);
            return (
              <li key={a.id} className="account-card" style={{ '--acc': a.color } as React.CSSProperties}>
                <span className="account-card__dot" />
                <div className="account-card__text">
                  <strong>{a.name}</strong>
                  <span className="muted">{a.email}</span>
                  <span className={`account-card__status ${s?.state === 'error' ? 'is-bad' : ''}`}>
                    {s?.authError
                      ? t('登录失效：{error}', { error: tMessage(s.error ?? '') })
                      : s?.state === 'error'
                        ? tMessage(s.error ?? '')
                        : s?.state === 'syncing' || s?.state === 'connecting'
                          ? t('正在同步…')
                          : s?.lastSync
                            ? t('连接正常 · {ago}同步', { ago: timeAgo(s.lastSync) })
                            : t('等待连接')}
                    {s?.push && s.state !== 'error' && t(' · 即时推送')}
                  </span>
                </div>
                <button className="btn btn--small btn--quiet" onClick={() => onEditAccount(a)}>
                  {t('编辑')}
                </button>
                <button className="btn btn--small btn--quiet tool--danger" onClick={() => void remove(a)} aria-label={t('删除账户 {email}', { email: a.email })}>
                  <Icon name="trash" size={15} />
                </button>
              </li>
            );
          })}
        </ul>
        <button className="btn btn--primary" onClick={() => onEditAccount('new')}>
          <Icon name="plus" size={16} /> {t('添加账户')}
        </button>
      </div>
    );
  } else if (tab === 'calendar') {
    body = <CalendarSettings />;
  } else if (tab === 'ai') {
    body = <AiSettings settings={settings} update={update} />;
  } else if (tab === 'storage') {
    body = <StorageSettings settings={settings} update={update} />;
  } else if (tab === 'notifications') {
    body = (
      <div className="settings__section">
        <h3 className="settings__h">{t('新邮件通知')}</h3>
        <Row title={t('新邮件时发系统通知')} desc={t('窗口收进托盘时也会提醒。点通知直接打开那封邮件。')}>
          <Switch checked={settings.notifications} onChange={(v) => void update({ notifications: v })} label={t('新邮件通知')} />
        </Row>
        <Row title={t('提示音')} desc={t('通知时播放系统提示音。')}>
          <Switch checked={settings.notificationSound} onChange={(v) => void update({ notificationSound: v })} label={t('提示音')} />
        </Row>
        <Row title={t('手机推送内容')} desc={t('推送会经过 ntfy 或 Google，可以少带一点内容。')}>
          <select className="setting__select" value={settings.pushPreview} onChange={(e) => void update({ pushPreview: e.target.value as PushPreview })}>
            <option value="full">{t('发件人、主题和摘要')}</option>
            <option value="sender">{t('发件人和主题')}</option>
            <option value="count">{t('只说有几封新邮件')}</option>
          </select>
        </Row>
        <Row title={t('暂停通知')} desc={paused ? pausedText : t('开会、专注时先别打扰。托盘右键菜单里也能暂停。')}>
          {paused ? (
            <button className="btn btn--small" onClick={() => void update({ notificationsPausedUntil: null })}>
              <Icon name="bell" size={15} /> {t('恢复通知')}
            </button>
          ) : (
            <div className="segmented">
              <button aria-pressed={false} onClick={() => void update({ notificationsPausedUntil: Date.now() + 3_600_000 })}>
                {t('1 小时')}
              </button>
              <button
                aria-pressed={false}
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() + 1);
                  d.setHours(8, 0, 0, 0);
                  void update({ notificationsPausedUntil: d.getTime() });
                }}
              >
                {t('到明早')}
              </button>
              <button aria-pressed={false} onClick={() => void update({ notificationsPausedUntil: Number.MAX_SAFE_INTEGER })}>
                {t('一直')}
              </button>
            </div>
          )}
        </Row>
      </div>
    );
  } else if (tab === 'appearance') {
    const themes: { id: SettingsT['theme']; label: string; icon: IconName }[] = [
      { id: 'system', label: t('跟随系统'), icon: 'monitor' },
      { id: 'light', label: t('浅色'), icon: 'sun' },
      { id: 'dark', label: t('深色'), icon: 'moon' },
    ];
    const fonts: { id: SettingsT['font']; label: string; desc: string; ui: 'marker' | 'system'; mail: 'marker' | 'system' }[] = [
      { id: 'all', label: t('全部用馍馍字体'), desc: t('界面和邮件都用'), ui: 'marker', mail: 'marker' },
      { id: 'ui', label: t('界面用馍馍字体'), desc: t('邮件用系统字体（默认）'), ui: 'marker', mail: 'system' },
      { id: 'system', label: t('全部用系统字体'), desc: t('界面和邮件都用'), ui: 'system', mail: 'system' },
    ];
    body = (
      <div className="settings__section">
        <h3 className="settings__h">{t('外观')}</h3>
        <div className="field__label">{t('配色主题')}</div>
        <div className="theme-cards" role="radiogroup" aria-label={t('配色主题')}>
          {themes.map((th) => (
            <button key={th.id} role="radio" aria-checked={settings.theme === th.id} className={`theme-card theme-card--${th.id}`} onClick={() => void update({ theme: th.id })}>
              <span className="theme-card__preview" aria-hidden>
                <i />
                <i />
                <i />
              </span>
              <span className="theme-card__label">
                <Icon name={th.icon} size={15} /> {th.label}
              </span>
            </button>
          ))}
        </div>
        <div className="field__label font-cards__title">{t('字体')}</div>
        <div className="font-cards" role="radiogroup" aria-label={t('字体')}>
          {fonts.map((f) => (
            <button key={f.id} role="radio" aria-checked={(settings.font ?? 'ui') === f.id} className="font-card" onClick={() => void update({ font: f.id })}>
              <span className="font-card__preview" aria-hidden>
                <span className={`font-card__ui font-card--${f.ui}`}>{t('收件箱')}</span>
                <span className={`font-card__mail font-card--${f.mail}`}>{t('邮件正文')}</span>
              </span>
              <span className="font-card__label">{f.label}</span>
              <span className="font-card__desc muted">{f.desc}</span>
            </button>
          ))}
        </div>
        <Row title={t('语言')} desc={t('跟随系统：按系统的语言选，没有这种语言时用英文。手机和电脑同步。想加一种语言？见仓库的 locales/README.md。')}>
          <select className="setting__select" value={settings.language} onChange={(e) => void update({ language: e.target.value })} aria-label={t('语言')}>
            <option value="system">{t('跟随系统')}</option>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </Row>
        <Row title={t('会话视图')} desc={t('同一个话题的往来邮件合成一条，打开时按时间排在一起。')}>
          <Switch checked={settings.conversationView} onChange={(v) => void update({ conversationView: v })} label={t('会话视图')} />
        </Row>
      </div>
    );
  } else if (tab === 'general') {
    body = (
      <div className="settings__section">
        <h3 className="settings__h">{t('通用')}</h3>
        {bridge.desktop && (
          <Row title={t('开机时自动启动')} desc={t('登录 Windows 后在托盘里悄悄启动，开始收信。（只对这台电脑生效）')}>
            <Switch checked={settings.launchAtLogin} onChange={(v) => void update({ launchAtLogin: v })} label={t('开机启动')} />
          </Row>
        )}
        {bridge.desktop && (
          <Row title={t('桌面小猫')} desc={t('馍馍在桌面和窗口上走来走去，新邮件用对话泡泡告诉你；点它有快捷操作，按住可以拎起来。关掉后通知换回弹窗。（只对这台电脑生效）')}>
            <Switch checked={settings.desktopMascot} onChange={(v) => void update({ desktopMascot: v })} label={t('桌面小猫')} />
          </Row>
        )}
        <Row title={t('检查新邮件的间隔')} desc={t('支持推送（IDLE）的邮箱会即时收到；这里是全部文件夹的定时检查。')}>
          <select className="setting__select" value={settings.pollMinutes} onChange={(e) => void update({ pollMinutes: Number(e.target.value) })}>
            {[2, 5, 10, 15, 30].map((m) => (
              <option key={m} value={m}>
                {t('每 {m} 分钟', { m })}
              </option>
            ))}
          </select>
        </Row>
        {bridge.desktop && (
          <Row title={t('关闭窗口')} desc={t('点 × 会把窗口收进托盘，继续收信和提醒。要彻底退出，请右键托盘图标选择退出。')}>
            <button className="btn btn--small btn--quiet" onClick={() => bridge.app.quit()}>
              <Icon name="power" size={15} /> {t('退出馍馍邮箱')}
            </button>
          </Row>
        )}
        {bridge.desktop && <UpdateSettings Row={Row} />}
        <p className="muted settings__version">
          {t('馍馍邮箱')} momomail {bridge?.app.version ?? ''}
        </p>
      </div>
    );
  } else if (tab === 'users' && me && !me.local && me.isAdmin) {
    body = <UserAdmin me={me} accounts={accounts} />;
  } else if (tab === 'connection') {
    body = <ConnectionSettings me={me} onMe={setMe} />;
  } else if (tab === 'shortcuts') {
    body = (
      <div className="settings__section">
        <h3 className="settings__h">{t('键盘快捷键')}</h3>
        <ShortcutTable />
      </div>
    );
  } else {
    body = <span className="spinner" />;
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('设置')} labelledBy="settings-title" className="settings">
      <div className="settings__layout">
        <div className="settings__tabs" role="tablist" aria-orientation="vertical">
          {tabs.map((x) => (
            <button
              key={x.id}
              role="tab"
              aria-selected={tab === x.id}
              className={`settings__tab ${tab === x.id ? 'is-active' : ''}`}
              onClick={() => {
                onTab(x.id);
                onEditAccount(null);
              }}
            >
              <Icon name={x.icon} size={16} />
              {x.label}
              {flags[x.id] && <span className={`settings__flag settings__flag--${flags[x.id]!.level}`} title={flags[x.id]!.title} aria-label={flags[x.id]!.title} />}
            </button>
          ))}
        </div>
        <div className="settings__panel" role="tabpanel">
          <div key={tab} className="settings__panel-in">
            {body}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
